import {useEffect, useMemo, useRef, useState} from 'react';
import './App.css';
import {handleMultipleJsonUpload} from "./handleMultipleUpload";
import {findSkippedSongs} from "./findSkippedSongs";
import {TopTracks} from "./getMostPlayedTracks";
import {getTotalTimeListened} from "./getTotalAmountListned";
import {getSpotifyRedirectUri, createSpotifyLoginUrl, completeSpotifyLogin} from "./spotifyAuth";
import SpotifyWebApi from 'spotify-web-api-js';
import Rewind from './Rewind';

function App() {
    const [showRewind, setShowRewind] = useState(window.location.hash === '#wrapped');
    const [jsonData, setJsonData] = useState(undefined); //All songs from multiple json files
    const [threshold, setThreshold] = useState(30000); // default 30 seconds
    const [skippedSongs, setSkippedSongs] = useState([]);
    const [onlyShowMostlySkipped, setOnlyShowMostlySkipped] = useState(false);
    const [playlistData, setPlaylistData] = useState(null);
    const [selectedPlaylist, setSelectedPlaylist] = useState(null);
    const [amountMostPlayedTracks, setAmountMostPlayedTracks] = useState(5);
    const [accessToken, setAccessToken] = useState(undefined);
    const [authError, setAuthError] = useState('');
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [listenToSongs, setListenToSongs] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sortType, setSortType] = useState('ratio'); // default
    const spotify = useMemo(() => new SpotifyWebApi(), []);

    const CLIENT_ID = '903e86d6d2fe46de8a3642b950623d9a';

    useEffect(() => {
        const handleNavigation = () => setShowRewind(window.location.hash === '#wrapped');
        window.addEventListener('hashchange', handleNavigation);
        return () => window.removeEventListener('hashchange', handleNavigation);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('code') || params.has('error')) return;
        const token = localStorage.getItem("spotify_access_token");
        if (token) {
            setAccessToken(token);
        }
    }, []);

    const loginSpotify = async () => {
        setAuthError('');
        setIsAuthenticating(true);
        try {
            const redirectUri = getSpotifyRedirectUri();
            if (window.location.hostname === 'localhost') {
                const localUrl = new URL(window.location.href);
                localUrl.hostname = '127.0.0.1';
                window.location.assign(localUrl.href);
                return;
            }
            window.location.assign(await createSpotifyLoginUrl(CLIENT_ID, redirectUri));
        } catch (error) {
            console.error('Spotify login failed:', error);
            setAuthError(`Unable to connect to Spotify: ${error.message}`);
            setIsAuthenticating(false);
        }
    };

    const tokenFetchedRef = useRef(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if ((params.has('code') || params.has('error')) && !tokenFetchedRef.current) {
            tokenFetchedRef.current = true;
            setIsAuthenticating(true);
            localStorage.removeItem('spotify_access_token');
            completeSpotifyLogin(CLIENT_ID, params)
                .then(token => {
                    localStorage.setItem('spotify_access_token', token);
                    setAccessToken(token);
                })
                .catch(error => {
                    console.error('Spotify login failed:', error);
                    setAuthError(error.message);
                })
                .finally(() => {
                    setIsAuthenticating(false);
                    window.history.replaceState({}, document.title, `/${window.location.hash}`);
                });
        }
    }, []);

    const getPlaylists = () => {
        if (!accessToken) return;
        spotify.setAccessToken(accessToken);

        spotify.getUserPlaylists()
            .then(data => {
                setPlaylistData(data);
            })
            .catch(err => {console.error("Error fetching playlists:", err);
                setAuthError('Unable to load Spotify playlists. Please reconnect to Spotify and try again.');
                localStorage.removeItem('spotify_access_token');
                setAccessToken(undefined)});
    };

    const playlistCache = useRef({});

    const fetchPlaylistTracks = async (playlistId, playlistTitle) => {
        if (!playlistId || !playlistTitle) return;

        console.log("Playlist:", playlistTitle);
        setIsLoading(true);
        setSelectedPlaylist(null)
        setSkippedSongs([]);

        if (playlistCache.current[playlistTitle]) {
            console.log("Loaded from cache:", playlistCache.current[playlistTitle]);
            setSelectedPlaylist({
                id: playlistId,
                tracks: playlistCache.current[playlistTitle],
            });
            setIsLoading(false);
            return;
        }

        spotify.setAccessToken(accessToken);

        let allTracks = [];
        let offset = 0;
        const limit = 100;

        try {
            while (true) {
                const response = await spotify.getPlaylistTracks(playlistId, {
                    offset,
                    limit,
                });

                const fetched = response.items.filter(item => item.track); // filter out nulls

                allTracks = allTracks.concat(fetched);
                if (fetched.length < limit) break;

                offset += limit;
            }

            const uniqueTracks = [
                ...new Map(
                    allTracks.map((item) => [item.track.id, item.track])
                ).values()
            ];

            const simplifiedTracks = uniqueTracks.map(track => ({
                name: track.name,
                artist: track.artists.map(a => a.name).join(', '),
                id: track.id,
                uri: track.uri
            }));

            playlistCache.current[playlistTitle] = simplifiedTracks;

            setSelectedPlaylist({ id: playlistId, tracks: simplifiedTracks });
            console.log("Fetched playlist tracks:", simplifiedTracks);
        } catch (error) {
            console.error("Failed to fetch playlist tracks", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteTrack = async (song) => {
        console.log(song);

        if (!song.uri) {
            console.log('No track uri');
            return
        };

        try {
            await spotify.removeTracksFromPlaylist(selectedPlaylist.id, [
                { uri: song.uri }
            ]);
            alert("Track removed successfully!");

            setSkippedSongs(prev => prev.filter(track => track.uri !== song.uri));

            setSelectedPlaylist(prev => {
                const updatedTracks = prev.tracks.filter(track => track.uri !== song.uri);
                return { ...prev, tracks: updatedTracks };
            });

        } catch (error) {
            console.error("Failed to remove track from playlist:", error);
        }
    };

    const visibleSkippedSongs = skippedSongs
        .filter(song => !onlyShowMostlySkipped || song.skipCount > song.playCount)
        .sort((a, b) => sortType === 'skipped'
            ? b.skipCount - a.skipCount
            : b.ratio - a.ratio);
    const totalSkipEvents = skippedSongs.reduce((total, song) => total + song.skipCount, 0);
    const totalPlayEvents = skippedSongs.reduce((total, song) => total + song.playCount, 0);
    const skipRate = totalSkipEvents + totalPlayEvents
        ? Math.round((totalSkipEvents / (totalSkipEvents + totalPlayEvents)) * 100)
        : 0;

    return (
        <div className="app-shell">
            <header className="topbar">
                <a className="brand" href="#overview" aria-label="Skip Signal home">
                    <span className="brand-mark" aria-hidden="true"><i /><i /><i /><i /></span>
                    <span>skip<span className="brand-accent">signal</span></span>
                </a>
                <nav className="top-nav" aria-label="Main navigation">
                    <a href="#overview">Overview</a>
                    <a href="#sources">Data sources</a>
                    <a href="#analysis">Skip analysis</a>
                    <a href="#report">Listening report</a>
                    <a href="#wrapped" aria-current={showRewind ? 'page' : undefined}>Your rewind</a>
                </nav>
                <div className="connection-area">
                    {accessToken ? (
                        <span className="connection-status"><span /> Spotify connected</span>
                    ) : (
                        <button className="button button-spotify button-small" disabled={isAuthenticating} onClick={loginSpotify}>
                            <span className="spotify-mark" aria-hidden="true">◉</span> {isAuthenticating ? 'Connecting...' : 'Connect Spotify'}
                        </button>
                    )}
                </div>
            </header>
            {authError && <p className="auth-notice" role="alert">{authError}</p>}
            {window.location.hostname === 'localhost' && <p className="auth-notice">For local Spotify login, open this site at <a href={window.location.href.replace('://localhost', '://127.0.0.1')}>127.0.0.1</a>, then click Connect Spotify. Changing host reloads the app; upload history afterwards.</p>}

            {showRewind ? (
                <Rewind data={jsonData} onUpload={event => handleMultipleJsonUpload(event, setJsonData)} />
            ) : (
            <main className="dashboard-main" id="overview">
                <section className="intro-row">
                    <div>
                        <p className="eyebrow"><span className="eyebrow-line" /> PERSONAL LISTENING ANALYTICS</p>
                        <h1>Your listening,<br /><span>decoded.</span></h1>
                        <p className="intro-copy">See what you skip, what stays on repeat, and how your listening adds up.</p>
                    </div>
                    <div className="intro-visual" aria-hidden="true">
                        <div className="visual-grid" />
                        <div className="visual-bars"><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /></div>
                        <div className="visual-caption"><span>YOUR LISTENING PROFILE</span><b>01 / 03</b></div>
                    </div>
                </section>

                <section className="metric-strip" aria-label="Listening overview">
                    <div className="metric-item">
                        <span className="metric-label">HISTORY EVENTS</span>
                        <strong>{jsonData ? jsonData.length.toLocaleString() : '—'}</strong>
                        <span className="metric-note">{jsonData ? 'plays in uploaded history' : 'upload a Spotify data export'}</span>
                    </div>
                    <div className="metric-item">
                        <span className="metric-label">PLAYLIST TRACKS</span>
                        <strong>{selectedPlaylist ? selectedPlaylist.tracks.length.toLocaleString() : '—'}</strong>
                        <span className="metric-note">{selectedPlaylist ? 'tracks in selected playlist' : 'connect and choose a playlist'}</span>
                    </div>
                    <div className="metric-item metric-highlight">
                        <span className="metric-label">SKIP RATE</span>
                        <strong>{skippedSongs.length ? `${skipRate}%` : '—'}</strong>
                        <span className="metric-note">{skippedSongs.length ? `${totalSkipEvents.toLocaleString()} early skips · ${totalPlayEvents.toLocaleString()} plays` : 'run an analysis to see your rate'}</span>
                    </div>
                </section>

                <section className="sources-section" id="sources">
                    <div className="section-heading">
                        <div><p className="eyebrow">01 / GET STARTED</p><h2>Build your report</h2></div>
                        <span className="section-note">Connect a playlist and add your listening history.</span>
                    </div>
                    <div className="source-grid">
                        <section className="source-panel">
                            <div className="source-heading">
                                <span className="source-index">A</span>
                                <div><h3>Spotify playlist</h3><p>Choose a playlist to compare against your history.</p></div>
                            </div>
                            {accessToken ? (
                                <div className="source-controls">
                                    {!playlistData ? (
                                        <button className="button button-spotify" onClick={getPlaylists}>Load my playlists <span aria-hidden="true">↗</span></button>
                                    ) : (
                                        <label className="field-label" htmlFor="playlist-select">
                                            YOUR PLAYLIST
                                            <select
                                                id="playlist-select"
                                                onChange={(e) => {
                                                    const selectedPlaylistId = e.target.value;
                                                    const selectedPlaylistName = e.target.options[e.target.selectedIndex].text;
                                                    fetchPlaylistTracks(selectedPlaylistId, selectedPlaylistName);
                                                }}
                                                value={selectedPlaylist?.id || ''}
                                            >
                                                <option value="" disabled>Select a playlist</option>
                                                {playlistData.items.map((pl, idx) => (
                                                    <option key={idx} value={pl.id}>{pl.name}</option>
                                                ))}
                                            </select>
                                        </label>
                                    )}
                                    {isLoading && <span className="loading-note">Loading playlist tracks...</span>}
                                </div>
                            ) : (
                                <div className="source-controls">
                                    <p className="source-hint">Spotify access lets Skip Signal read your playlists and remove tracks you no longer want.</p>
                                    <button className="button button-spotify" disabled={isAuthenticating} onClick={loginSpotify}>{isAuthenticating ? 'Connecting...' : 'Connect Spotify'} <span aria-hidden="true">↗</span></button>
                                </div>
                            )}
                        </section>

                        <section className="source-panel upload-panel">
                            <div className="source-heading">
                                <span className="source-index">B</span>
                                <div><h3>Listening history</h3><p>Use your Spotify extended streaming history JSON files.</p></div>
                            </div>
                            <label className="upload-control" htmlFor="history-upload">
                                <span className="upload-icon" aria-hidden="true">↑</span>
                                <span><strong>{jsonData ? 'History loaded' : 'Choose JSON files'}</strong><small>{jsonData ? `${jsonData.length.toLocaleString()} listening events imported` : 'Select one or more files from your data export'}</small></span>
                                <span className="upload-action">{jsonData ? 'Replace' : 'Browse'}</span>
                                <input id="history-upload" type="file" accept=".json,application/json" multiple onChange={(event) => handleMultipleJsonUpload(event, setJsonData)} />
                            </label>
                        </section>
                    </div>
                </section>

                <div className="analysis-layout">
                    <section className="analysis-panel" id="analysis">
                        <div className="section-heading analysis-heading">
                            <div><p className="eyebrow">02 / TRACK-BY-TRACK</p><h2>Skip analysis</h2></div>
                            <span className="result-count">{skippedSongs.length ? `${visibleSkippedSongs.length} TRACKS` : 'AWAITING DATA'}</span>
                        </div>
                        <div className="analysis-controls">
                            <label className="field-label threshold-field" htmlFor="threshold-input">
                                PLAYED UNDER
                                <span className="input-with-unit"><input id="threshold-input" type="number" min="0" step="1000" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} /><span>ms</span></span>
                            </label>
                            <label className="field-label" htmlFor="sort-select">
                                RANK BY
                                <select id="sort-select" value={sortType} onChange={e => setSortType(e.target.value)}>
                                    <option value="ratio">Skip / play ratio</option>
                                    <option value="skipped">Most skipped</option>
                                </select>
                            </label>
                            <label className="toggle-control">
                                <input type="checkbox" checked={onlyShowMostlySkipped} onChange={(e) => setOnlyShowMostlySkipped(e.target.checked)} />
                                <span className="toggle-track" aria-hidden="true" />
                                <span>Mostly skipped</span>
                            </label>
                            <button className="button button-primary" disabled={!selectedPlaylist || !jsonData} onClick={() => findSkippedSongs(selectedPlaylist, threshold, jsonData, setSkippedSongs)}>
                                Analyze history <span aria-hidden="true">→</span>
                            </button>
                        </div>

                        {isLoading ? <div className="empty-state"><span className="loading-indicator" /><p>Loading playlist tracks...</p></div> : null}
                        {!isLoading && skippedSongs.length === 0 && (
                            <div className="empty-state">
                                <div className="empty-wave" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /></div>
                                <h3>{jsonData && selectedPlaylist ? 'Ready when you are' : 'Your report starts here'}</h3>
                                <p>{jsonData && selectedPlaylist ? 'Run the analysis to compare your listening history with this playlist.' : 'Connect Spotify, select a playlist, and upload your listening history to find early skips.'}</p>
                            </div>
                        )}
                        {!isLoading && visibleSkippedSongs.length > 0 && (
                            <div className="song-list">
                                {visibleSkippedSongs.map((song, idx) => {
                                    const total = song.skipCount + song.playCount;
                                    const skippedWidth = total ? (song.skipCount / total) * 100 : 0;
                                    const skipTone = song.skipCount > song.playCount ? 'high' : song.skipCount === song.playCount ? 'even' : 'low';

                                    return (
                                        <article className="song-row" key={song.id || `${song.displayName}-${idx}`}>
                                            <span className="song-rank">{String(idx + 1).padStart(2, '0')}</span>
                                            <div className="song-info"><strong>{song.trackName}</strong><span>{song.artistName}</span></div>
                                            <div className="song-distribution" aria-label={`${song.skipCount} skipped, ${song.playCount} played`}>
                                                <div className="distribution-bar"><span style={{ width: `${skippedWidth}%` }} /></div>
                                                <span className="distribution-label"><b>{song.skipCount}</b> skip <i /> <b>{song.playCount}</b> play</span>
                                            </div>
                                            <span className={`ratio-value ${skipTone}`}>{song.ratio.toFixed(2)}<small> ratio</small></span>
                                            <div className="song-actions">
                                                <button className="text-button" onClick={() => setListenToSongs(song.id)}>Listen</button>
                                                <button className="text-button remove-button" onClick={() => handleDeleteTrack(song)}>Remove</button>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    <aside className="report-column" id="report">
                        <section className="report-panel">
                            <div className="section-heading report-heading">
                                <div><p className="eyebrow">03 / YOUR NUMBERS</p><h2>Listening report</h2></div>
                                <span className="report-mark" aria-hidden="true">↗</span>
                            </div>
                            {jsonData ? (
                                <>
                                    <div className="listening-total"><span>TOTAL LISTENING TIME</span><strong>{getTotalTimeListened(jsonData)}</strong><small>Across {jsonData.length.toLocaleString()} history events</small></div>
                                    <div className="top-tracks-heading">
                                        <h3>Most played</h3>
                                        <label className="field-label compact-field" htmlFor="top-track-count">SHOW
                                            <input id="top-track-count" type="number" min="1" max="20" value={amountMostPlayedTracks} onChange={(e) => setAmountMostPlayedTracks(Number(e.target.value))} />
                                        </label>
                                    </div>
                                    <TopTracks data={jsonData} amount={amountMostPlayedTracks} />
                                    <a className="button button-primary" href="#wrapped">See your rewind</a>
                                </>
                            ) : (
                                <div className="report-empty"><span className="report-empty-mark" aria-hidden="true">⌁</span><p>Upload your listening history to see total time and your most-played tracks.</p></div>
                            )}
                        </section>
                        <section className="player-panel">
                            <div className="player-heading"><span className="live-dot" /><span>NOW PLAYING</span><span className="player-rule" /></div>
                            <iframe
                                title="Spotify player"
                                src={listenToSongs ? `https://open.spotify.com/embed/track/${listenToSongs}` : 'https://open.spotify.com/embed/track/4PTG3Z6ehGkBFwjybzWkR8?si=1ee91af22a7543a6'}
                                width="100%"
                                height="152"
                                frameBorder="0"
                                allow="encrypted-media"
                            />
                        </section>
                    </aside>
                </div>
                <footer className="page-footer"><span>SKIP SIGNAL <i /> LISTEN CLOSELY</span><span>PERSONAL LISTENING, MADE VISIBLE</span></footer>
            </main>
            )}
        </div>
    );
}

export default App;
